/**
 * Address/text formatting helpers.
 *
 * Business listings store `address` as a structured object
 * `{ street, street2, city, zip }` and a back-compat string in `location`.
 * Older code (and event records created during the transition) may have
 * stored the object itself in fields that are supposed to be plain strings,
 * which renders as the dreaded "[object Object]" in JSX or popup HTML.
 *
 * `formatListingAddress` accepts either shape and returns a clean string.
 * `safeText` is a defensive last-line-of-defense for rendering arbitrary
 * values that the UI assumes are strings.
 */

export function formatListingAddress(input) {
  if (!input) return "";
  if (typeof input === "string") return input.trim();
  if (typeof input !== "object") return String(input);
  const { street, street2, city, state, zip } = input;
  return [street, street2, city, state, zip]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean)
    .join(", ");
}

export function safeText(value, fallback = "") {
  if (value == null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    // Most commonly an address object slipped through. Flatten it.
    const flat = formatListingAddress(value);
    return flat || fallback;
  }
  return fallback;
}
